import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getSocketManager, type WsConnectionState } from '@/lib/socketManager';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@reflex/ui/lib/utils';
import type { ServerStatus } from '@/lib/types';
import type { AdminServerStatus } from '@reflex/shared';
import { useVersionStatus } from '@/hooks/ui/useVersionCheck';
import { useAuth } from '@/hooks/auth/useAuth';
import { Radio, Globe, Server, Activity, ChevronRight } from 'lucide-react';
import { PanelTitle, SectionLabel } from '@reflex/ui/components/layout/Typography';
import { StatusRow } from './StatusRow';
import { VersionSection } from './VersionSection';
import { WebhookActivitySection } from './WebhookActivitySection';
import { stateConfig } from './connectionState';

function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return '/api';
  const base = import.meta.env.VITE_API_BASE_URL || '/api';
  return base.startsWith('http') ? base : `${window.location.origin}${base}`;
}

export function StatusPanel({
  state,
  status,
}: {
  state: WsConnectionState;
  // Kept for back-compat with story fixtures; currently unused since the
  // public `/status` only exposes `version` (already covered by
  // useVersionStatus).
  status?: ServerStatus | undefined;
}) {
  void status;
  const wsUrl = getSocketManager().endpoint;
  const cfg = stateConfig[state];
  const versionStatus = useVersionStatus();
  const { isPlatformAdmin } = useAuth();
  const [webhookEnabled, setWebhookEnabled] = useState(false);

  // Operational config (Runloop API URL, secret-presence) lives behind
  // `admin:read` — fetched only when the caller has the bit (audit M4).
  const { data: adminStatus } = useQuery<AdminServerStatus>({
    queryKey: queryKeys.adminServerStatus,
    queryFn: api.getAdminStatus,
    staleTime: 60_000,
    enabled: isPlatformAdmin && state === 'open',
    retry: false,
  });

  const {
    data: webhookActivity,
    isLoading: webhookLoading,
    refetch: refetchWebhooks,
  } = useQuery({
    queryKey: queryKeys.webhookActivity,
    queryFn: api.getWebhookActivity,
    enabled: webhookEnabled,
    staleTime: 10_000,
    retry: false,
  });

  const handleWebhookRefresh = () => {
    if (!webhookEnabled) {
      setWebhookEnabled(true);
    } else {
      refetchWebhooks();
    }
  };

  return (
    <div className="flex flex-col gap-4 w-72">
      <div className="flex items-center gap-2">
        <span className={cn('inline-block h-2.5 w-2.5 rounded-full shrink-0', cfg.color)} />
        <PanelTitle as="span" size="sm" className="font-semibold">
          {cfg.label}
        </PanelTitle>
      </div>

      <VersionSection
        serverVersion={versionStatus.serverVersion}
        clientVersion={versionStatus.clientVersion}
        serverCommit={versionStatus.serverCommit}
        clientCommit={versionStatus.clientCommit}
        updateAvailable={versionStatus.updateAvailable}
      />

      {isPlatformAdmin && (
        <>
          <div className="space-y-2">
            <SectionLabel as="h4" className="flex items-center gap-1.5">
              <Radio className="h-3.5 w-3.5" />
              Endpoints
            </SectionLabel>
            <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2.5">
              <StatusRow icon={Radio} label="WebSocket" value={wsUrl} />
              <StatusRow icon={Globe} label="REST API" value={getApiBaseUrl()} />
              {adminStatus && (
                <StatusRow icon={Server} label="Runloop API" value={adminStatus.runloopBaseUrl} />
              )}
            </div>
          </div>

          <div className="space-y-2">
            <SectionLabel as="h4" className="flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5" />
              Integration Health
            </SectionLabel>
            <Link
              to="/admin/plugins"
              className="flex items-center justify-between rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground hover:bg-muted/40 transition-colors"
            >
              <span>View plugin health in Admin</span>
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          <WebhookActivitySection
            activity={webhookActivity}
            isLoading={webhookLoading}
            onRefresh={handleWebhookRefresh}
          />
        </>
      )}
    </div>
  );
}
